import { useEffect } from 'react';
import { cn } from '@rocco/utils/cn';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks';
import { useFollowSelectorContext } from '~/modules/moodboards/context/FollowSelectorContext';
import type { PinItemType } from '~/modules/moodboards/type';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useUserLoggedIn } from '@modules/user';
import { DETAIL_CTA_ICON_SIZE } from './detail-cta';

interface DetailSaveButtonProps {
    /** The pinned item id (professional / product / project ID). */
    pinId: number;
    pinType: PinItemType;
    /** `light` = dark hairline on a white section; `dark` = white on a dark section. */
    theme?: 'light' | 'dark';
    /** Extra classes (e.g. a page-specific size override). */
    className?: string;
    /**
     * Render as a labelled pill rather than the square icon button. For
     * sections that offer Save as one of a pair of named actions, where an
     * unlabelled bookmark would be the only control the reader has to guess at.
     */
    label?: string;
    /**
     * A square at rest that grows its label out on hover and keyboard focus
     * — Save, or Saved once it is, the tile save's own words. For the sticky
     * CTA clusters, where the bookmark sits beside named pills and a
     * permanent label would double up on the pair. Ignored when `label` is
     * set.
     */
    hoverLabel?: boolean;
    /**
     * Overrides the accessible name. A detail page has one save button and
     * "Save" says enough; a list has one per row, where it does not. Pass a
     * function to name the file and still say whether it is already saved,
     * which only this component knows.
     */
    ariaLabel?: string | ((isSaved: boolean) => string);
    /**
     * How the unsaved state is drawn. `outline` is the detail CTA cluster's
     * hairline bookmark; `white` is the lifted white pill the download rows
     * put beside their Download control, so the pair reads as one set. The
     * saved state is filled either way, which is what marks it as on.
     */
    tone?: 'outline' | 'white';
    /**
     * Run when the reader asks to save, before the board picker opens. For a
     * gated file, whose lead is otherwise only raised by downloading it.
     */
    onSaveIntent?: () => void;
}

/**
 * The 52px bookmark / Save pill that opens the FollowSelector (design-board)
 * flow, matching the design-system CTA height. Shared by the professional,
 * product and project detail CTAs. Relies on the global FollowSelectorProvider
 * (rendered in AppPage).
 */
export const DetailSaveButton = ({
    pinId,
    pinType,
    theme = 'light',
    className,
    label,
    hoverLabel = false,
    ariaLabel,
    tone = 'outline',
    onSaveIntent,
}: DetailSaveButtonProps) => {
    const { isInDesignBoard } = useHandleFollow();
    const { isLoggedIn } = useUserLoggedIn();
    const { ensureMembershipLoaded } = useFollowSelectorContext();
    const { followSelector, openFollowSelector } = useFollowSelectorProxy({
        pinId,
        pinType,
    });
    const isFavourited = pinId ? isInDesignBoard(pinId, pinType) : false;
    const isDark = theme === 'dark';

    // The root list of saved page IDs can lag a pin write (Redis). Ask this
    // pin directly so a reload still shows Saved.
    useEffect(() => {
        if (!isLoggedIn || !pinId) {
            return;
        }
        void ensureMembershipLoaded(pinId, pinType);
    }, [ensureMembershipLoaded, isLoggedIn, pinId, pinType]);

    return (
        <>
            <button
                type="button"
                aria-label={
                    typeof ariaLabel === 'function'
                        ? ariaLabel(isFavourited)
                        : (ariaLabel ?? (isFavourited ? 'Saved' : 'Save'))
                }
                aria-pressed={isFavourited}
                onClick={(event) => {
                    event.preventDefault();
                    onSaveIntent?.();
                    openFollowSelector(event);
                }}
                className={cn(
                    'detail-cta-pill inline-flex shrink-0 items-center justify-center border transition-colors',
                    label
                        ? 'h-[3.25rem] gap-[0.625rem] rounded-[var(--radius-btn-l)] px-[1.5rem] text-base leading-none font-medium'
                        : hoverLabel
                          ? // A square at rest — the icon plus this padding is
                            // the pill's own 52px — that widens as the label
                            // grows out on hover.
                            'group h-[3.25rem] rounded-[var(--radius-btn-l)] px-[0.875rem]'
                          : DETAIL_CTA_ICON_SIZE,
                    isDark
                        ? isFavourited
                            ? 'border-white bg-white text-[#181818]'
                            : 'border-white/60 text-white'
                        : isFavourited
                          ? 'border-[#181818] bg-[#181818] text-white'
                          : tone === 'white'
                            ? 'border-transparent bg-white text-[#181818] shadow-[0px_4px_14px_10px_rgba(0,0,0,0.05)]'
                            : 'border-[#181818] text-[#181818]',
                    className,
                    // Re-apply the filled chip after `className` so a caller
                    // ground (the project's sticky `bg-black/40`) cannot
                    // tailwind-merge it away and leave a dark bookmark on
                    // dark glass.
                    isDark &&
                        isFavourited &&
                        'border-white bg-white text-[#181818]',
                )}
            >
                <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className={cn(
                        label
                            ? 'size-[1.125rem]'
                            : 'max-md:size-5 md:size-[1.5rem]',
                    )}
                    fill={isFavourited ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth="1.6"
                >
                    <path d="M6 4h12v16l-6-4-6 4V4z" />
                </svg>
                {label ? <span>{isFavourited ? 'Saved' : label}</span> : null}
                {!label && hoverLabel ? (
                    // Collapsed to nothing at rest; the pill widens as it
                    // grows out. Padding sits on the inner span so the closed
                    // state truly measures zero.
                    <span
                        className={cn(
                            'block max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 ease-out',
                            'group-hover:max-w-[5rem] group-hover:opacity-100',
                            'group-focus-visible:max-w-[5rem] group-focus-visible:opacity-100',
                            'motion-reduce:transition-none',
                        )}
                    >
                        <span className="block pl-[0.625rem] text-base leading-none font-medium">
                            {isFavourited ? 'Saved' : 'Save'}
                        </span>
                    </span>
                ) : null}
            </button>
            {followSelector}
        </>
    );
};
